import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import '@angular/localize/init';

import { BehaviorSubject } from 'rxjs';
import { Authentication } from '../models/authentication.interface';
import { User } from '../models/user.interface';
import { RolePermission } from '../models/role.permission.enum';
import { storageVariableNames } from '../constants/storageVariableNames';

export abstract class AuthService {
  public apiUrl: string = document.location.origin + '/api';

  public userValue: Authentication;

  // Observable navItem source
  static _authNavStatusSource = new BehaviorSubject<string>(null);
  static _authUserSource = new BehaviorSubject<User>(null);
  private static _impersonatedDealer = new BehaviorSubject<number>(null);
  // Observable navItem stream
  static authNavStatus$ = AuthService._authNavStatusSource.asObservable();
  static authUser$ = AuthService._authUserSource.asObservable();
  static impersonatedDealer$ = AuthService._impersonatedDealer.asObservable();

  static authToken: string;

  constructor(protected http: HttpClient) {
    let role: string = null;
    let authUser: User;

    // check if we are refreshing page
    if (!AuthService.authToken) {
      AuthService.authToken = localStorage.getItem('auth_token');
    }

    if (AuthService.authToken) {
      let jwt = AuthService.parseJwt(AuthService.authToken);
      if (jwt && jwt.user) {
        role = jwt.rol;
        authUser = jwt.user ? JSON.parse(jwt.user, this.fixPropertyCase) as User : null;
      }
    }

    AuthService._authUserSource.next(authUser);

    if (AuthService._authNavStatusSource.value != role)
      AuthService._authNavStatusSource.next(role);
  }

  public static parseJwt(token): any {
    var base64Url = token.split('.')[1];
    if (!base64Url) return null;

    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }; 

  public getDealerId(): number {
    let isIDIAdmin = (AuthService._authNavStatusSource.value == 'IDI');
    let authUser = AuthService._authUserSource.value;

    if (authUser == null) return null;
    return isIDIAdmin ? AuthService._impersonatedDealer.value : authUser.dealer_ID;
  }

  public setDealerId(dealerId: number) {
    let isIDIAdmin = (AuthService._authNavStatusSource.value == 'IDI');
    let authUser = AuthService._authUserSource.value;

    if (authUser == null || !isIDIAdmin) return;
    AuthService._impersonatedDealer.next(dealerId);
  }

  public loginUser(res: Authentication) {
    sessionStorage.removeItem('full');
    sessionStorage.removeItem(storageVariableNames.calendarView);
    localStorage.setItem('auth_token', res.auth_token);
    AuthService.authToken = res.auth_token;

    AuthService._authUserSource.next(JSON.parse(res.user, this.fixPropertyCase) as User);
    AuthService._authNavStatusSource.next(res.rol);

  }

  public static hasPermission(permId: RolePermission): boolean {
    let user = this._authUserSource.value;

    if ((permId == RolePermission.AddJob || permId == RolePermission.EditJob) && user.isQuoteOnly)
      return false;

    return (user != null && user.role != null && user.role.rolePerms != null
      && user.role.rolePerms.find(p => p.perm_ID == permId) != null);
  }

  public static logout(router: Router) {
    AuthService.authToken = null;
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('full');
    sessionStorage.removeItem(storageVariableNames.calendarView);

    AuthService._authUserSource.next(null);
    AuthService._authNavStatusSource.next(null);

    router.navigate(['/login']);
  }

  public isLoggedIn(): boolean {
    return (AuthService._authNavStatusSource.value !== null);
  }

  checkIsLoggedIn(): boolean {
    // check if we are refreshing page
    if (!AuthService.authToken) {
      AuthService.authToken = localStorage.getItem('auth_token');
    }

    if (AuthService.authToken) {
      let jwt = AuthService.parseJwt(AuthService.authToken);

      if (Date.now() < jwt.exp * 1000) {
        return true;
      }
    }
  }

  protected fixPropertyCase(prop: string, value: string): string {
    var lower = prop.charAt(0).toLowerCase() + prop.slice(1);
    if (prop === lower) return value;
    else this[lower] = value;
  }
}
